import React from 'react';

import { usePublicCompany } from 'client/hooks';
import RatingEmoji from 'components/RatingEmoji';
import { currentLanguage } from 'helpers/utils';
import { Card, Icon, Message, Popup } from 'ui';

import Step from './Step';
import StepGroup from './StepGroup';

type Props = {
  ticket: BackendPublic.TicketDetail;
  steps: BackendPublic.TicketDetail['process_steps'] | null;
};

function Header({ ticket, steps }: Props) {
  const company = usePublicCompany();
  return (
    <Card flat>
      <Card.Content>
        <div className="space-y-4">
          <div className="space-y-3">
            <div>
              <h2 className="text-2xl font-medium">
                {currentLanguage(ticket.type)} {ticket.code}
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-gray-600 md:gap-12">
              <div className="flex items-center gap-1 text-sm">
                <Icon size="small" name="user" color="gray" solid colorIntensity={600} />
                <span>{ticket.customer_name}</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <Icon size="small" name="shopping-cart" color="gray" solid colorIntensity={600} />
                <span>{ticket.order_id ?? gettext('Ticket without order')}</span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <Icon size="small" name="calendar" color="gray" solid colorIntensity={600} />
                <Popup on="hover" trigger={<span>{moment(ticket.created_at).fromNow()}</span>}>
                  <span className="whitespace-nowrap">{moment(ticket.created_at).format('ll')}</span>
                </Popup>
              </div>
            </div>
          </div>

          {ticket.closed_at ? (
            <>
              <Message warning>
                <strong>{gettext('This ticket is closed.')}</strong>
              </Message>
              {ticket.customer_rating && (
                <Card flat>
                  <div className="p-5">
                    <div className="flex items-baseline justify-between">
                      <div className="flex items-center mb-2">
                        <strong className="pr-2">{gettext('Your rating')}</strong>
                        <RatingEmoji rating={ticket.customer_rating as 0 | 1 | 2} size="small" />
                      </div>
                    </div>
                    <p>{ticket.customer_rating_comment}</p>
                    {ticket.customer_rating_comment_response && (
                      <div className="mt-4 p-4 bg-gray-100 border border-gray-300 rounded-lg shadow">
                        <div className="flex items-center mb-2">
                          <Icon name="reply" className="text-gray-700 w-5 h-5 mr-2" />
                          <p className="font-semibold text-gray-800">{currentLanguage(company.company?.name)}</p>
                        </div>
                        <p className="text-gray-800">{ticket.customer_rating_comment_response}</p>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </>
          ) : (
            steps && (
              <div>
                <StepGroup>
                  {steps.map((step) => (
                    <Step key={step.id} active={ticket.process_step === step.id}>
                      {currentLanguage(step.name)}
                    </Step>
                  ))}
                </StepGroup>
              </div>
            )
          )}
        </div>
      </Card.Content>
    </Card>
  );
}

export default Header;
